import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';

interface AddGuestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const AddGuestModal: React.FC<AddGuestModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [fullName, setFullName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [events, setEvents] = useState<string[]>(['IE', 'ES', 'PB']);
    const [plusOneAllowed, setPlusOneAllowed] = useState(true);
    const [plusChildren, setPlusChildren] = useState(false);
    const [country, setCountry] = useState('IE');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const upsertGuest = httpsCallable(functions, 'upsertGuest');
            await upsertGuest({
                fullName,
                inviteCode: inviteCode || undefined, // Send undefined if empty to auto-generate
                events,
                plusOneAllowed,
                plusChildren,
                country
            });
            onSuccess();
            onClose();
            // Reset form
            setFullName('');
            setInviteCode('');
            setEvents(['IE', 'ES', 'PB']);
            setPlusOneAllowed(true);
            setPlusChildren(false);
            setCountry('IE');
        } catch (err: any) {
            setError(err.message || 'Failed to add guest');
        } finally {
            setLoading(false);
        }
    };

    const toggleEvent = (code: string) => {
        setEvents(prev =>
            prev.includes(code)
                ? prev.filter(c => c !== code)
                : [...prev, code]
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add New Guest">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-sans text-secondary">
                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-base">
                        {error}
                    </div>
                )}

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                        Full Name
                    </label>
                    <input
                        type="text"
                        required
                        className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                        value={fullName}
                        onChange={e => setFullName(e.target.value)}
                        placeholder="e.g. Jane Doe"
                    />
                </div>


                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                        Invite Code <span className="font-normal normal-case opacity-60">(Optional)</span>
                    </label>
                    <input
                        type="text"
                        className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-mono"
                        value={inviteCode}
                        onChange={e => setInviteCode(e.target.value.toUpperCase())}
                        placeholder="Leave empty to auto-generate"
                        style={{ textTransform: 'uppercase' }}
                    />
                </div>

                <div className="space-y-2">
                    <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Events</span>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={events.includes('IE')}
                                onChange={() => toggleEvent('IE')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">Ireland (IE)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={events.includes('ES')}
                                onChange={() => toggleEvent('ES')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">Spain (ES)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={events.includes('PB')}
                                onChange={() => toggleEvent('PB')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">Pre-Boda (PB)</span>
                        </label>
                    </div>
                </div>

                <div className="space-y-2">
                    <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Country</span>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="country"
                                className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                checked={country === 'IE'}
                                onChange={() => setCountry('IE')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">Ireland (IE)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="country"
                                className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                checked={country === 'ES'}
                                onChange={() => setCountry('ES')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">Spain (ES)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="country"
                                className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                checked={country === 'UK'}
                                onChange={() => setCountry('UK')}
                            />
                            <span className="text-base group-hover:text-primary transition-colors">UK (UK)</span>
                        </label>
                    </div>
                </div>

                <div className="pt-2 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                            type="checkbox"
                            className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                            checked={plusOneAllowed}
                            onChange={e => setPlusOneAllowed(e.target.checked)}
                        />
                        <span className="text-base font-medium group-hover:text-primary transition-colors">Allow Plus One?</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                            type="checkbox"
                            className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                            checked={plusChildren}
                            onChange={e => setPlusChildren(e.target.checked)}
                        />
                        <span className="text-base font-medium group-hover:text-primary transition-colors">Allow Children?</span>
                    </label>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button type="button" className="btn btn-ghost px-6 py-2 rounded-full text-base font-bold border border-gray-200 hover:bg-gray-50 transition-colors" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary px-6 py-2 rounded-full text-base font-bold bg-primary text-white hover:bg-primary-dark transition-all shadow-md active:scale-95" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Guest'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
